import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  PermissionGroup,
  PermissionPresetEntity,
} from '@features/roles-permissions';
import {
  AccordionComponent,
  ExpansionPanelComponent,
  CheckboxComponent,
  ChipComponent,
  ButtonComponent,
} from '@presentation/components';
import { TooltipDirective } from '@presentation/directives';

@Component({
  selector: 'cm-permissions-configurator',
  standalone: true,
  imports: [
    CommonModule,
    AccordionComponent,
    ExpansionPanelComponent,
    CheckboxComponent,
    ChipComponent,
    ButtonComponent,
    TooltipDirective,
  ],
  templateUrl: './permissions-configurator.component.html',
  styleUrl: './permissions-configurator.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionsConfiguratorComponent {
  public readonly title = input<string>('Permissions');
  public readonly hideHeader = input<boolean>(false);
  public readonly permissions = input<PermissionGroup[]>([]);
  public readonly presets = input<PermissionPresetEntity[]>([]);
  public readonly selectedPermissionIds = input<string[]>([]);
  public readonly disabledPermissionIds = input<string[]>([]);
  public readonly disabled = input<boolean>(false);
  public readonly readOnly = input<boolean>(false);
  public readonly disabledReasons = input<Record<string, string>>({});

  public readonly selectedPermissionIdsChange = output<string[]>();

  protected readonly permissionGroupVms = computed(() => {
    const selected = new Set(this.selectedPermissionIds());
    const disabledIds = new Set(this.disabledPermissionIds());
    const reasons = this.disabledReasons();

    return this.permissions().map(group => {
      const permissionVms = group.permissions.map(p => ({
        ...p,
        isSelected: selected.has(p.id),
        isDisabled: disabledIds.has(p.id),
        disabledReason: reasons[p.id] ?? null,
      }));
      const selectedCount = permissionVms.filter(p => p.isSelected).length;
      return {
        ...group,
        permissions: permissionVms,
        selectedCount,
        description: `${selectedCount}/${group.permissions.length} selected`,
        isFullySelected: selectedCount === group.permissions.length,
      };
    });
  });

  /**
   * Returns the count of selected permissions for a given group.
   */
  public getGroupSelectedCount(group: PermissionGroup): number {
    const selected = this.selectedPermissionIds();
    return group.permissions.filter((p) => selected.includes(p.id)).length;
  }

  /**
   * Returns formatted description like "3/10 selected".
   */
  public getGroupDescription(group: PermissionGroup): string {
    const selectedCount = this.getGroupSelectedCount(group);
    const totalCount = group.permissions.length;
    return `${selectedCount}/${totalCount} selected`;
  }

  /**
   * Checks if a permission is currently selected.
   */
  public isPermissionSelected(permissionId: string): boolean {
    return this.selectedPermissionIds().includes(permissionId);
  }

  /**
   * Checks if a specific permission is disabled by its ID.
   */
  public isPermissionDisabled(permissionId: string): boolean {
    return this.disabledPermissionIds().includes(permissionId);
  }

  /**
   * Returns the reason a permission is disabled (for 508c tooltip), or null if not applicable.
   */
  public getDisabledReason(permissionId: string): string | null {
    return this.disabledReasons()[permissionId] ?? null;
  }

  /**
   * Track function for the inner @for loop over permissions.
   * Keys on both id and disabled state so Angular remounts when a permission becomes disabled/enabled,
   * preventing stale internal state in cm-checkbox. Keyed on disabled (not selected) to avoid
   * remounting the gate checkbox itself when the user unchecks it, which would lose keyboard focus.
   */
  public trackPermission(permission: { id: string }): string {
    return `${permission.id}|${this.isPermissionDisabled(permission.id)}`;
  }

  /**
   * Toggles a single permission.
   */
  public togglePermission(permissionId: string): void {
    if (this.disabled() || this.readOnly()) return;

    const current = [...this.selectedPermissionIds()];
    const index = current.indexOf(permissionId);
    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(permissionId);
    }
    this.selectedPermissionIdsChange.emit(current);
  }

  /**
   * Selects all permissions in a group.
   */
  public selectAllInGroup(group: PermissionGroup): void {
    if (this.disabled() || this.readOnly()) return;

    const current = new Set(this.selectedPermissionIds());
    for (const permission of group.permissions) {
      current.add(permission.id);
    }
    this.selectedPermissionIdsChange.emit(Array.from(current));
  }

  /**
   * Clears all permissions in a group.
   */
  public clearAllInGroup(group: PermissionGroup): void {
    if (this.disabled() || this.readOnly()) return;

    const groupIds = new Set(group.permissions.map((p) => p.id));
    const remaining = this.selectedPermissionIds().filter(
      (id) => !groupIds.has(id),
    );
    this.selectedPermissionIdsChange.emit(remaining);
  }

  /**
   * Applies a preset's permissions.
   */
  public applyPreset(preset: PermissionPresetEntity): void {
    if (this.disabled() || this.readOnly()) return;
    this.selectedPermissionIdsChange.emit([...preset.permissionIds]);
  }

  /**
   * Checks if all permissions in a group are selected.
   */
  public isGroupFullySelected(group: PermissionGroup): boolean {
    const selected = this.selectedPermissionIds();
    return group.permissions.every((p) => selected.includes(p.id));
  }

  /**
   * Returns only selected permissions for a group (used in readOnly mode).
   */
  public getSelectedPermissionsInGroup(group: PermissionGroup): string[] {
    const selected = this.selectedPermissionIds();
    return group.permissions
      .filter((p) => selected.includes(p.id))
      .map((p) => p.name);
  }
}
